// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT
import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { Column, DataRow } from '@lfx/shared/interfaces';
import { MetricChartEntityService, MetricChartData } from './metric-chart-base.entity';
import { EcosystemMetricType } from '@lfx/key-projects/key-projects.model';

// Technical Influence Entities
import { MaintainersMetricEntity } from './maintainers-metric.entity';
import { ContributorsMetricEntity } from './contributors-metric.entity';
import { CommitActivities } from './commit-activities.entity';
import { PullRequestsOpenedMetricEntity } from './pull-requests-opened-metric.entity';
import { AverageTimeToMergePrs } from './avg-time-to-merge-prs.entity';

// Ecosystem Influence Entities
import { BoardMembersMetricEntity } from './board-members-metric.entity';
import { CommitteeMembersMetricEntity } from './committee-members-metric.entity';
import { MeetingAttendanceMetricEntity } from './meeting-attendance-metric.entity';
import { EventAttendanceMetricEntity } from './event-attendance-metric.entity';
import { EventSpeakersMetricEntity } from './event-speakers-metric.entity';
import { EventSponsorshipsMetricEntity } from './event-sponsorships-metric.entity';
import { MeetupAttendanceMetricEntity } from './meetup-attendance-metric.entity';
import { CollaborationActivityMetricEntity } from './collaboration-activity-metric.entity';
import { CertifiedIndividualsMetricEntity } from './certified-individuals-metric.entity';

/**
 * Supported metric types
 */
export type MetricType = 'maintainers' | 'contributors' | 'commit_activities' | 'pull_requests_opened' | 'avg_time_to_merge_prs' | EcosystemMetricType;

/**
 * Registry service for metric chart entities
 * Handles delegation to appropriate entity service based on metric type
 */
@Injectable({
  providedIn: 'root'
})
export class MetricChartEntityRegistry {
  // Technical Influence Entities
  private readonly maintainersEntity = inject(MaintainersMetricEntity);
  private readonly contributorsEntity = inject(ContributorsMetricEntity);
  private readonly commitActivitiesEntity = inject(CommitActivities);
  private readonly pullRequestsOpenedEntity = inject(PullRequestsOpenedMetricEntity);
  private readonly avgTimeToMergePrsEntity = inject(AverageTimeToMergePrs);

  // Ecosystem Influence Entities
  private readonly boardMembersEntity = inject(BoardMembersMetricEntity);
  private readonly committeeMembersEntity = inject(CommitteeMembersMetricEntity);
  private readonly meetingAttendanceEntity = inject(MeetingAttendanceMetricEntity);
  private readonly eventAttendanceEntity = inject(EventAttendanceMetricEntity);
  private readonly eventSpeakersEntity = inject(EventSpeakersMetricEntity);
  private readonly eventSponsorshipsEntity = inject(EventSponsorshipsMetricEntity);
  private readonly meetupAttendanceEntity = inject(MeetupAttendanceMetricEntity);
  private readonly collaborationActivityEntity = inject(CollaborationActivityMetricEntity);
  private readonly certifiedIndividualsEntity = inject(CertifiedIndividualsMetricEntity);

  // Registry map for entity services
  private readonly entityMap = new Map<MetricType, MetricChartEntityService>([
    // Technical Influence Metrics
    ['maintainers', this.maintainersEntity],
    ['contributors', this.contributorsEntity],
    ['commit_activities', this.commitActivitiesEntity],
    ['pull_requests_opened', this.pullRequestsOpenedEntity],
    ['avg_time_to_merge_prs', this.avgTimeToMergePrsEntity],
    
    // Ecosystem Influence Metrics
    ['board_members', this.boardMembersEntity],
    ['committee_members', this.committeeMembersEntity],
    ['meeting_attendance', this.meetingAttendanceEntity],
    ['event_attendance', this.eventAttendanceEntity],
    ['event_speakers', this.eventSpeakersEntity],
    ['event_sponsorships', this.eventSponsorshipsEntity],
    ['meetup_attendance', this.meetupAttendanceEntity],
    ['collaboration_activity', this.collaborationActivityEntity],
    ['certified_individuals', this.certifiedIndividualsEntity],
  ]);

  /**
   * Get columns for a specific metric type
   */
  getColumns(metricType?: MetricType): Column[] {
    if (!metricType) {
      return this.getDefaultColumns();
    }

    const entity = this.entityMap.get(metricType);
    return entity ? entity.getColumns() : this.getDefaultColumns();
  }

  /**
   * Fetch data for a specific metric type
   */
  fetchData(metricType?: MetricType, params: MetricChartData = {}): Observable<DataRow[]> {
    if (!metricType) {
      return this.getDefaultData();
    }

    const entity = this.entityMap.get(metricType);
    return entity ? entity.fetchData(params) : this.getDefaultData();
  }

  /**
   * Check if a metric type is supported
   */
  isSupported(metricType?: string): metricType is MetricType {
    return !!(metricType && this.entityMap.has(metricType as MetricType));
  }

  /**
   * Get all supported metric types
   */
  getSupportedMetricTypes(): MetricType[] {
    return Array.from(this.entityMap.keys());
  }

  /**
   * Get summary table data for metric chart offcanvas
   * Returns both columns and data for the summary table
   * If metricType is supported and entity has getSummaryData method, use that
   * Otherwise return default mock data
   */
  getSummaryTableData(metricType?: MetricType, params: MetricChartData = {}): Observable<{columns: Column[], data: DataRow[]}> {
    // Check if metric type is supported and has summary data method
    if (metricType && this.isSupported(metricType)) {
      const entity = this.entityMap.get(metricType);
      if (entity && entity.getSummaryData) {
        return entity.getSummaryData(params);
      }
    }

    // Return default mock data for unsupported types or entities without summary
    const summaryColumns: Column[] = [
      { name: 'Definition', prop: 'definition' },
      { name: 'Total count for this project', prop: 'totalCount' },
      { name: 'Data source', prop: 'dataSource' }
    ];

    const summaryData: DataRow[] = [
      {
        definition: { text: 'We define contribution in Insights' },
        totalCount: { text: '10,000' },
        dataSource: { text: 'Github' }
      }
    ];

    return of({ columns: summaryColumns, data: summaryData });
  }

  /**
   * Default columns for unsupported metric types
   */
  private getDefaultColumns(): Column[] {
    return [
      { name: 'Our Contributor', prop: 'contributor' },
      { name: 'Username', prop: 'username' },
      { name: 'First activity', prop: 'firstActivity' },
      { name: 'Most recent', prop: 'mostRecent' },
      { name: '# Contributions', prop: 'contributions' }
    ];
  }

  /**
   * Default data for unsupported metric types
   */
  private getDefaultData(): Observable<DataRow[]> {
    return of([
      {
        contributor: { text: 'Jessica Liu', extraData: { avatarUrl: 'assets/img/avatars/jessica.jpg' } },
        username: { text: '@jessicaliu' },
        firstActivity: { text: 'Jan 2023' },
        mostRecent: { text: 'Aug 2025' },
        contributions: { text: '14' }
      },
      {
        contributor: { text: 'Penual Craig', extraData: { avatarUrl: 'assets/img/avatars/penual.jpg' } },
        username: { text: '@penualcraig' },
        firstActivity: { text: 'Mar 2018' },
        mostRecent: { text: 'Aug 2025' },
        contributions: { text: '29' }
      },
      {
        contributor: { text: 'Merganah Mettlinson', extraData: { avatarUrl: 'assets/img/avatars/merganah.jpg' } },
        username: { text: '@merganahm' },
        firstActivity: { text: 'Nov 2024' },
        mostRecent: { text: 'Jul 2025' },
        contributions: { text: '6' }
      }
    ]);
  }
}
